// BarLineComboChart.tsx
import {
  Box,
  Typography,
  Stack,
  debounce,
  StackProps,
} from "@mui/material";
import ReactECharts from "echarts-for-react";
import LoaderComponent from "components/Loader";
import { useEffect, useRef, useState, useMemo } from "react";
import { useTheme } from "@mui/material/styles";
import WidgetWraper from "components/Common/WidgetWraper";
import { InfoTooltip } from "../InfoTooltip";
import { buildBarLineComboOptions } from "../chartWidgetUtility";
import HighlightedText from "components/GlobalSearch/HighlightText";

type ComboSeries = {
  key: string;
  name: string;
  type: "bar" | "line";
  color?: string;
  yAxis?: "left" | "right"; // default: bar->left, line->right
  smooth?: boolean;
  symbolSize?: number;
};

interface CustomBarChartProps {
  title: any;
  subTitle?: string;
  isLoading?: boolean;
  data: any[];
  containerProps?: StackProps;
  isCost?: boolean;
  xKey?: string;
  xAxisLabel?: string;
  series: ComboSeries[];
  tooltipLabel?:
    | string
    | JSX.Element
    | { title: string; description: string | React.ReactNode }[];
  hasTooltip?: boolean;
  showGridLines?: boolean;
  tooltipFormatter?: (params: any) => string;
  rightAxisType?: "dollar" | "number" | "percent";
  onBarClick?: (params: any) => void;
}

const BarLineComboChart = ({
  title,
  subTitle,
  isLoading,
  data,
  containerProps,
  isCost = false,
  xKey = "name",
  xAxisLabel = "Month",
  series,
  tooltipLabel,
  hasTooltip = true,
  showGridLines = false,
  tooltipFormatter,
  rightAxisType = "percent",
  onBarClick,
}: Readonly<CustomBarChartProps>) => {
  const theme = useTheme();
  const colors = theme.palette;
  const [windowSize, setWindowSize] = useState(window.innerWidth);
  const chartContainerRef = useRef<HTMLDivElement>(null);

  const defaultOptions = {
    graphic: [
      {
        type: "rect",
        left: "center",
        top: "center",
        shape: { width: 300, height: 200 },
        style: {
          fill: colors.widget.background.card,
        },
      },
    ],
    title: {
      show: true,
      text: "No data",
      textStyle: {
        color: colors.widget.text.main,
        fontSize: 16,
        fontWeight: "normal",
      },
      left: "center",
      top: "center",
    },
    backgroundColor: colors.widget.background.card,
  };

  const dynamicOptions = useMemo(() => {
    const result = buildBarLineComboOptions({
      data,
      colors,
      showGridLines,
      isCost,
      xKey,
      xAxisLabel,
      series,
      tooltipFormatter,
      rightAxisType,
    });

    return result || defaultOptions;
  }, [
    data,
    colors,
    showGridLines,
    isCost,
    xKey,
    xAxisLabel,
    series,
    tooltipFormatter,
  ]);

  const finalOptions = data?.length ? dynamicOptions : defaultOptions;

  useEffect(() => {
    const handleResize = debounce(() => {
      if (chartContainerRef.current) {
        setWindowSize(window.innerWidth);
      }
    }, 200);

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  return (
    <WidgetWraper>
      <Box
        sx={{ backgroundColor: `${colors.widget.background.card} !important` }}
        {...containerProps}
      >
        <Stack direction="column" gap={2}>
          <Stack direction="row" alignItems="center">
            <Typography
              variant="widgetTitleNew"
              sx={{ color: colors.widget.text.title }}
            >
              <HighlightedText text={title} />
            </Typography>

            {hasTooltip && !!tooltipLabel && (
              <InfoTooltip label={tooltipLabel} />
            )}
          </Stack>

          {subTitle && (
            <Typography
              variant="bodyText"
              sx={{ color: colors.widget.text.title }}
            >
              {subTitle}
            </Typography>
          )}
        </Stack>

        <Box
          sx={{ width: "100%", height: isCost ? 319 : 350 }}
          mt={2}
          key={windowSize}
          ref={chartContainerRef}
        >
          {!isLoading ? (
            <ReactECharts
              option={finalOptions}
              style={{ width: "100%", height: "100%" }}
              onEvents={{
                click: (params: any) => onBarClick?.(params),
              }}
            />
          ) : (
            <LoaderComponent pt={20} />
          )}
        </Box>
      </Box>
    </WidgetWraper>
  );
};

export default BarLineComboChart;
